import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import Header from '../components/shared/Header'
import Footer from '../components/shared/Footer'
import MetaTags from '../components/shared/MetaTags'
import StructuredData from '../components/shared/StructuredData'
import ShareButtons from '../components/shared/ShareButtons'
import { sanitizeHTML } from '../utils/sanitize'

export default function BlogPostPage() {
  const { slug } = useParams()
  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPublication = async () => {
      try {
        const response = await axios.get(`/api/v1/research/${slug}`)
        setPost(response.data)
      } catch (error) {
        console.error('Failed to fetch observation:', error)
      } finally {
        setLoading(false)
      }
    }

    if (slug) {
      fetchPublication()
    }
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-container mx-auto px-5 md:px-8 py-8">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-muted-dark">Loading research analysis...</div>
          </div>
        </div>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-container mx-auto px-5 md:px-8 py-8">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-2xl mb-4">Observation not found</h1>
              <button
                onClick={() => window.location.href = '/notes'}
                className="text-accent-dark hover:underline"
              >
                Return to Observations
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <MetaTags
        title={`${post.title} - Observations | deedslab.com`}
        description={post.excerpt}
        url={`/notes/${post.slug}`}
        type="article"
      />
      <StructuredData
        type="Article"
        title={`${post.title} - Observations | deedslab.com`}
        description={post.excerpt}
        url={`/notes/${post.slug}`}
        datePublished={post.published_at}
        dateModified={post.updated_at}
        experimentData={null}
      />

      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-8">
          <Header />

          {/* Article Header */}
          <header className="py-8 md:py-12">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="font-mono text-xs tracking-wide uppercase text-accent-dark bg-accent-dark/10 px-3 py-1 rounded">
                  Observation
                </span>
                {post.research_area && (
                  <span className="font-mono text-xs tracking-wide uppercase text-accent-light bg-accent-light/10 px-2 py-1 rounded">
                    {post.research_area}
                  </span>
                )}
                {post.methodology && (
                  <span className="font-mono text-xs tracking-wide uppercase text-muted-dark bg-surface-dark-2 px-2 py-1 rounded">
                    {post.methodology}
                  </span>
                )}
                {post.validation_status && (
                  <span className={`font-mono text-xs tracking-wide uppercase px-2 py-1 rounded ${
                    post.validation_status === 'validated'
                      ? 'text-green-400 bg-green-400/10'
                      : post.validation_status === 'peer-reviewed'
                      ? 'text-blue-400 bg-blue-400/10'
                      : 'text-yellow-400 bg-yellow-400/10'
                  }`}>
                    {post.validation_status}
                  </span>
                )}
              </div>

              <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                {post.title}
              </h1>

              <p className="text-lg text-muted-dark leading-relaxed mb-6 max-w-3xl">
                {post.excerpt}
              </p>

              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4 text-sm text-muted-dark">
                  <span>By <strong className="text-text-dark">{post.author}</strong></span>
                  {post.published_at && (
                    <span>
                      {new Date(post.published_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  )}
                  {post.reading_time && (
                    <span className="font-mono">{post.reading_time} min read</span>
                  )}
                </div>

                <ShareButtons
                  title={`${post.title} - Research Findings | deedslab.com`}
                  description={post.excerpt}
                  url={`/research/${post.slug}`}
                />
              </div>
            </div>
          </header>

          {/* Article Content */}
          <article className="prose prose-lg prose-invert mx-auto max-w-none">
            <div
              className="text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(post.content) }}
            />
          </article>

          {/* Research Sections */}
          {(post.findings || post.implications) && (
            <div className="mt-8 md:mt-12 space-y-6">
              {post.findings && (
                <section className="border border-accent-dark/30 rounded-lg p-6 bg-accent-dark/5">
                  <h2 className="text-lg font-semibold mb-4 text-accent-dark font-mono uppercase tracking-wide">
                    Key Findings
                  </h2>
                  <div className="prose prose-sm prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(post.findings) }} />
                  </div>
                </section>
              )}

              {post.implications && (
                <section className="border border-accent-dark/30 rounded-lg p-6 bg-accent-dark/5">
                  <h2 className="text-lg font-semibold mb-4 text-accent-dark font-mono uppercase tracking-wide">
                    Practical Implications
                  </h2>
                  <div className="prose prose-sm prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(post.implications) }} />
                  </div>
                </section>
              )}
            </div>
          )}

          {/* Related Experiments */}
          {post.experiment_ids && post.experiment_ids.length > 0 && (
            <section className="mt-8 md:mt-12">
              <h2 className="text-lg font-semibold mb-4 font-mono uppercase tracking-wide text-muted-dark">
                Related Experiments
              </h2>
              <div className="flex flex-wrap gap-2">
                {post.experiment_ids.map((expId, index) => (
                  <a
                    key={index}
                    href={`/experiments/${expId}`}
                    className="inline-flex items-center gap-2 px-3 py-1 bg-surface-dark-1 border border-border-dark rounded hover:border-accent-dark/50 transition-colors"
                  >
                    <span className="font-mono text-xs">EXP-{expId.split('-')[1] || expId}</span>
                    <span className="text-xs">→</span>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Article Footer */}
          <footer className="py-8 md:py-12 mt-8 md:mt-12 border-t border-border-dark">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-dark">Published in</span>
                <a href="/notes" className="text-accent-dark hover:underline font-medium">
                  Observations
                </a>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-dark">Share this note:</span>
                <ShareButtons
                  title={`${post.title} - Experimental Note | deedslab.com`}
                  description={post.excerpt}
                  url={`/notes/${post.slug}`}
                />
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-border-dark/50">
              <div className="text-center">
                <p className="text-sm text-muted-dark mb-4">
                  This experimental note represents observations from controlled investigation.
                  Insights are based on empirical data collected through iterative experimental protocols.
                  All observations are documented through systematic testing and reproducible methodologies.
                </p>
                <a
                  href="/notes"
                  className="inline-flex items-center gap-2 text-accent-dark hover:underline font-medium"
                >
                  <span>← Back to Observations</span>
                </a>
              </div>
            </div>
          </footer>

          <Footer />
        </div>
      </div>
    </>
  )
}
